import { useTranslation } from 'next-i18next';
import type { BearingsTableProps } from '@hub/bearing-hub/BearingHubPage/BearingsTable/BearingsTable';
import { BearingsTableHead } from '@hub/bearing-hub/BearingHubPage/BearingsTable/BearingsTableHead';
import { BearingTableExpandableRow } from '@hub/bearing-hub/BearingHubPage/BearingsTable/BearingTableExpandableRow';
import type { HeaderDefinition } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import {
  IbhQueryStatusHandler,
  QueryStatusName,
} from '@hub/bearing-hub/common/ui/IbhQueryStatusHandler/IbhQueryStatusHandler';
import { IBH_PRODUCTS_PER_PAGE } from '@hub/libs/constants';

type BearingsDesktopTableProps = Pick<
  BearingsTableProps,
  | 'isLoading'
  | 'lastKnownValidResponse'
  | 'currentPage'
  | 'maxLifeTimeReference'
  | 'bearingPartsResponseStatus'
  | 'quantity'
  | 'selectedShape'
  | 'setActiveModal'
  | 'clientReady'
  | 'unitSystem'
  | 'articlesAddingToBasket'
  | 'addToShoppingCart'
  | 'bearingPartsLifetime'
  | 'isLifetimeLoading'
> & {
  tableData: BearingsTableProps['lastKnownValidResponse'];
  tableHeaders: HeaderDefinition[];
  expandedRows: Set<string>;
  selectedParts: string[];
  onExpandRow: (articleNumber: string, isExpanded: boolean) => void;
  onSelectPart: (articleNumber: string) => void;
  onClearSelection: () => void;
};

export const BearingsDesktopTable = ({
  isLoading,
  lastKnownValidResponse,
  currentPage,
  maxLifeTimeReference,
  bearingPartsResponseStatus,
  quantity,
  selectedShape,
  setActiveModal,
  clientReady,
  unitSystem,
  articlesAddingToBasket,
  addToShoppingCart,
  bearingPartsLifetime,
  isLifetimeLoading,
  tableData,
  tableHeaders,
  expandedRows,
  selectedParts,
  onExpandRow,
  onSelectPart,
  onClearSelection,
}: BearingsDesktopTableProps) => {
  const { t } = useTranslation('bearing-hub/bearingHub');
  const status = isLoading ? QueryStatusName.pending : bearingPartsResponseStatus;
  const showRows = status === QueryStatusName.success || status === QueryStatusName.idle;

  return (
    <table id="product-table" className="w-full" data-testid="bearings-table">
      <BearingsTableHead
        selectedShape={selectedShape}
        selectedParts={selectedParts}
        lastKnownValidResponse={lastKnownValidResponse}
        setActiveModal={setActiveModal}
        unitSystem={unitSystem}
        clearSelections={onClearSelection}
      />
      {showRows ? (
        <tbody className="text-center" data-testid="bearings-table-body">
          {tableData
            .slice(IBH_PRODUCTS_PER_PAGE * (currentPage - 1), IBH_PRODUCTS_PER_PAGE * currentPage)
            .map(product => (
              <BearingTableExpandableRow
                isCustom={product.articleNumber.startsWith('TEMP-')}
                quantity={quantity}
                key={product.articleNumber}
                data={product}
                maxLifeTimeReference={maxLifeTimeReference}
                selectedShape={selectedShape}
                setActiveModal={setActiveModal}
                unitSystem={unitSystem}
                clientReady={clientReady}
                isExpanded={expandedRows.has(product.articleNumber)}
                expandChange={onExpandRow}
                tableHeaders={tableHeaders}
                selectedParts={selectedParts}
                articlesAddingToBasket={articlesAddingToBasket}
                addToShoppingCart={addToShoppingCart}
                selectPart={onSelectPart}
                lifetime={bearingPartsLifetime?.lifetimes?.[product.articleNumber]}
                isLifetimeLoading={isLifetimeLoading}
              />
            ))}
        </tbody>
      ) : (
        <tbody>
          <tr>
            <td
              colSpan={7}
              className={`relative p-0 align-top ${status === QueryStatusName.pending ? 'h-[500px]' : ''}`}
              aria-label={
                status === QueryStatusName.pending
                  ? t('COMMON.DATA_LOADING')
                  : t('TOAST.ERROR_OCCURRED', { ns: 'toast/toast' })
              }
            >
              <IbhQueryStatusHandler
                status={status}
                style={{
                  minHeight: '500px',
                  border: 'solid 1px #AFAFAF',
                  borderTop: '0',
                  top: '0',
                  left: '0',
                }}
                loaderText={t('COMMON.DATA_LOADING')}
              >
                <span />
              </IbhQueryStatusHandler>
            </td>
          </tr>
        </tbody>
      )}
    </table>
  );
};
